/** @jsxImportSource @emotion/react */
import { Container, Grid, Stack, Text, useTheme } from "basis";
import { Separator } from "components/Separator/Separator";
import { formatAmount } from "library/moneyUtils";
import { ReactNode } from "react";
import { getMinMonthlyPayment } from "../../../components/IncreaseCreditLimit/utils";
import { validateNewCreditLimitRegex } from "../constants/constants";

interface Props {
  creditLimit: number;
  cardMinMonthlyPaymentPercentage: number;
  cardMinMonthlyPaymentThreshold: number;
  newCreditLimit: string;
  message?: {
    newLimit: string;
    newMonthlyPayment: string;
    oldLimit: string;
    oldMonthlyPayment: string;
    increase: string;
    increasePayment: string;
  };
}

export const NzCreditLimitDetails = ({
  creditLimit,
  cardMinMonthlyPaymentPercentage,
  cardMinMonthlyPaymentThreshold,
  newCreditLimit,
  message = {
    newLimit: "Requested credit limit",
    newMonthlyPayment: "Minimum monthly payment",
    oldLimit: "Current credit limit",
    oldMonthlyPayment: "Minimum monthly payment",
    increase: "Limit increase of",
    increasePayment: "Monthly Payment increase of",
  },
}: Props) => {
  const isNewCreditLimitValid =
    validateNewCreditLimitRegex.test(newCreditLimit);
  const symbolWhenInvalid = "-";
  const currentPayment = getMinMonthlyPayment({
    creditLimit,
    minMonthlyPaymentPercentage: cardMinMonthlyPaymentPercentage,
    minMonthlyPaymentThreshold: cardMinMonthlyPaymentThreshold,
  });
  const newPayment = getMinMonthlyPayment({
    creditLimit: newCreditLimit,
    minMonthlyPaymentPercentage: cardMinMonthlyPaymentPercentage,
    minMonthlyPaymentThreshold: cardMinMonthlyPaymentThreshold,
  });
  const limitIncreaseOf = isNewCreditLimitValid
    ? Number(newCreditLimit) - creditLimit
    : 0;
  const minMonthlyPaymentIncreaseOf = newPayment - currentPayment;

  return (
    <Container hasBorder padding="4">
      <Grid cols-md="1fr 1px 1fr 1px 1fr">
        <Stack gap="2" margin="0 0 4 0" margin-md="0 4 0 0">
          <ColumnWrapper>
            <Text>{message.oldLimit}</Text>
            <Text testId="old-credit-limit">{formatAmount(creditLimit)}</Text>
          </ColumnWrapper>
          <ColumnWrapper>
            <Text>{message.oldMonthlyPayment}</Text>
            <Text testId="old-minimum-monthly-payment">
              {formatAmount(currentPayment)}
            </Text>
          </ColumnWrapper>
        </Stack>
        <ColumnSeparator />
        <Stack gap="2" margin="4 0 4 0" margin-md="0 4 0 4">
          <ColumnWrapper>
            <Text>{message.newLimit}</Text>
            <Text testId="new-credit-limit">
              <strong>
                {isNewCreditLimitValid
                  ? formatAmount(newCreditLimit)
                  : symbolWhenInvalid}
              </strong>
            </Text>
          </ColumnWrapper>
          <ColumnWrapper>
            <Text>{message.newMonthlyPayment}</Text>
            <Text testId="minimum-monthly-payment">
              <strong>
                {isNewCreditLimitValid
                  ? formatAmount(newPayment)
                  : symbolWhenInvalid}
              </strong>
            </Text>
          </ColumnWrapper>
        </Stack>
        <ColumnSeparator />
        <Stack gap="2" margin="4 0 0 0" margin-md="0 0 0 4">
          <ColumnWrapper>
            <Text>{message.increase}</Text>
            <Text testId="increase">
              <strong>
                {limitIncreaseOf > 0
                  ? formatAmount(limitIncreaseOf)
                  : symbolWhenInvalid}
              </strong>
            </Text>
          </ColumnWrapper>
          <ColumnWrapper>
            <Text>{message.increasePayment}</Text>
            <Text testId="increase-monthly-payment">
              <strong>
                {isNewCreditLimitValid && minMonthlyPaymentIncreaseOf > 0
                  ? formatAmount(minMonthlyPaymentIncreaseOf)
                  : symbolWhenInvalid}
              </strong>
            </Text>
          </ColumnWrapper>
        </Stack>
      </Grid>
    </Container>
  );
};

const ColumnWrapper = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  return (
    <div
      css={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        [theme.minMediaQueries.md]: {
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }}
    >
      {children}
    </div>
  );
};

const ColumnSeparator = () => {
  const theme = useTheme();
  return (
    <div>
      <div
        css={{
          [theme.minMediaQueries.md]: {
            display: "none",
          },
        }}
      >
        <Separator />
      </div>
      <div
        css={{
          display: "none",
          [theme.minMediaQueries.md]: {
            display: "initial",
          },
        }}
      >
        <Separator vertical />
      </div>
    </div>
  );
};
